import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import Logo from './Logo';

const NAV_LINKS = ['Home', 'About', 'Services', 'Projects', 'Training', 'Contact'];

const LINK_ROUTES = { Home: '/', Projects: '/projects', Training: '/training' };

const isActive = (item, pathname) => {
  const route = LINK_ROUTES[item];
  return route ? pathname === route : false;
};

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLink = (item) => {
    setMobileOpen(false);
    if (item === 'Projects') { navigate('/projects'); return; }
    if (item === 'Training') { navigate('/training'); return; }
    if (isHome) {
      document.getElementById(item.toLowerCase())?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const transparent = !scrolled && isHome;

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-[#060D1F]/95 backdrop-blur-xl shadow-sm border-b border-white/10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => navigate('/')}><Logo size="md" /></button>

          {/* desktop links */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((item) => {
              const active = isActive(item, location.pathname);
              return (
                <button
                  key={item}
                  onClick={() => handleLink(item)}
                  className={`text-sm font-medium transition-colors relative group ${
                    active
                      ? 'text-orange-400'
                      : transparent ? 'text-white/85 hover:text-orange-400' : 'text-slate-200 hover:text-orange-400'
                  }`}
                >
                  {item}
                  <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-orange-500 rounded-full transition-all duration-300 ${
                    active ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </button>
              );
            })}
          </div>

          {/* CTA */}
          <div className="hidden lg:block">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleLink('Contact')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>

          {/* hamburger */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2">
            {mobileOpen
              ? <X className={`w-6 h-6 ${transparent ? 'text-white' : 'text-slate-200'}`} />
              : <Menu className={`w-6 h-6 ${transparent ? 'text-white' : 'text-slate-200'}`} />
            }
          </button>
        </div>

        {/* mobile menu */}
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="lg:hidden bg-[#060D1F] border-t border-white/10 py-3 rounded-b-xl shadow-xl"
          >
            {NAV_LINKS.map((item) => {
              const active = isActive(item, location.pathname);
              return (
                <button
                  key={item}
                  onClick={() => handleLink(item)}
                  className={`block w-full text-left px-6 py-3 font-medium text-sm transition-colors ${
                    active
                      ? 'text-orange-400 bg-orange-500/10'
                      : 'text-slate-200 hover:text-orange-400 hover:bg-orange-500/10'
                  }`}
                >
                  {item}
                </button>
              );
            })}
            <div className="px-6 pt-3 pb-2">
              <button
                onClick={() => { handleLink('Contact'); }}
                className="w-full bg-orange-500 text-white py-2.5 rounded-lg text-sm font-semibold"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}
